import { useContext, useEffect } from "react";
import useLocations from "../../api/hooks/useLocations";
import useModels from "../../api/hooks/useModels";
import { logMsgBgColors } from "../../consts/misc";
import { toIcons } from "../../consts/Paths";
import MainContext from "../../context/MainContext";
import useIsMobile from "../../hooks/useIsMobile";
import useToggle from "../../hooks/useToggle";
import { Location } from "../../interfaces";
import { formatDateYYYMMGG } from "../../utils/reformatDate";
import CustomInput from "../inputs/CustomInput";

const LocationModal = (props: any) => {
  const { model, setRefresh } = useContext(MainContext);
  const { isMobile } = useIsMobile();
  const {
    logMessage,
    form,
    onEdit,
    setOnEdit,
    locationSelected,
    setLocationSelected,
    deleteModalToggler,
    setDeleteType,
    tempLoading,
    messageOnScreen,
  } = props;

  const { RESPONSES, RESULTS, CTAs } = useLocations();

  const handleCloseModal = () => {
    props.toggler.toggle();
    setOnEdit(false);
    setLocationSelected(null);
    form.reset();
  };

  const handleOnDeleteClick = () => {
    setDeleteType("location");
    deleteModalToggler.toggle();
  };

  const manageLocation = (data: any) => {
    tempLoading.setIsToggled(true);
    if (onEdit && model && locationSelected) {
      const finalObj: Location = {
        related_model_id: model.id,
        ...data,
      };
      /* console.log(finalObj); */
      CTAs.updateLocation(locationSelected.id, finalObj);
    } else { 
      const finalObj: Location = {
        related_model_id: model?.id,
        ...data,
      };
      /* console.log( finalObj); */
      CTAs.createLocation(finalObj);
    }
  };

  useEffect(() => {
    /* console.log(RESPONSES.createLocationResponse, RESULTS.createLocationResult); */
    if (RESPONSES.createLocationResponse?.ok) {
      tempLoading.setIsToggled(false);
      messageOnScreen.toggle();
      logMessage.write("subject", "Location ");
      logMessage.write("other_text", "has been created ");
      logMessage.write("complement", "successfully!");
      logMessage.write("bgColor", logMsgBgColors.correct);
      setRefresh((prevState) => !prevState);
      handleCloseModal();
    } else if (RESPONSES.createLocationResponse?.ok === false) {
      tempLoading.setIsToggled(false);
      messageOnScreen.toggle();
      logMessage.write(
        "subject",
        `Error ${RESPONSES.createLocationResponse?.status}: `
      );
      logMessage.write(
        "other_text",
        `${RESPONSES.createLocationResponse?.statusText}`
      );
      logMessage.write("bgColor", logMsgBgColors.error);
    }
  }, [RESPONSES.createLocationResponse]);

  useEffect(() => {
    /* console.log(RESPONSES.updateLocationResponse, RESULTS.updateLocationResult); */
    if (RESPONSES.updateLocationResponse?.ok) {
      tempLoading.setIsToggled(false);
      messageOnScreen.toggle();
      logMessage.write("subject", "Location ");
      logMessage.write("other_text", "has been updated ");
      logMessage.write("complement", "successfully!");
      logMessage.write("bgColor", logMsgBgColors.correct);
      setRefresh((prevState) => !prevState);
      handleCloseModal();
    } else if (RESPONSES.updateLocationResponse?.ok === false) {
      tempLoading.setIsToggled(false);
      messageOnScreen.toggle();
      logMessage.write(
        "subject",
        `Error ${RESPONSES.updateLocationResponse?.status}: `
      );
      logMessage.write(
        "other_text",
        `${RESPONSES.updateLocationResponse?.statusText}`
      );
      logMessage.write("bgColor", logMsgBgColors.error);
    }
  }, [RESPONSES.updateLocationResponse]);

  return (
    <div
      className="modal-wrapper"
      style={{
        height: `100vh`,
        zIndex: deleteModalToggler.isToggled ? "2" : "",
      }}
    >
      <div
        className="location-modal"
        style={{
          width: isMobile ? "calc(345px - (72px + 29px))" : "",
          height: isMobile ? "520px" : "",
        }}
      >
        <div className="modal-header">
          <div>
            <p className="modal-title">Location for the period</p>
          </div>
          <div className="Oval" onClick={handleCloseModal}>
            <img src={`${toIcons}/close.svg`} />
          </div>
        </div>

        <form onSubmit={form.handleSubmit(manageLocation)}>
          <div
            className="customInputText-box"
            style={{ width: `100%`, marginTop: isMobile ? "25px" : "55px" }}
          >
            <div style={{ marginTop: isMobile ? "-10px" : "-40px" }}>
              <CustomInput
                title={"Location of the model"}
                name={"location"}
                type={"text"}
                register={form.register}
                errors={form.formState.errors}
                locationIcon={true}
                placeholder="e.g. Italy - Milan"
                width={isMobile ? "111%" : "104%"}
                defaultValue={onEdit ? locationSelected?.location : ""}
                constraints={{
                  required: true,
                  validate: (value: string) => value.trim() !== "",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "unset" : "center",
                justifyContent: "space-between",
                marginTop: "25px",
                gap: isMobile ? "20px" : "",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "11px",
                }}
              >
                <CustomInput
                  title={"Starts"}
                  name={"starting_date"}
                  type={"Date"}
                  register={form.register}
                  errors={form.formState.errors}
                  width={isMobile ? "243px" : "275px"}
                  noIcon={true}
                  defaultValue={
                    onEdit &&
                    formatDateYYYMMGG(new Date(locationSelected.starting_date))
                  }
                  constraints={{
                    required: true,
                  }}
                />
              </div>
              {!isMobile && <p className="mini-separator">-</p>}
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "11px",
                }}
              >
                <CustomInput
                  title={"Ends"}
                  name={"ending_date"}
                  type={"Date"}
                  register={form.register}
                  errors={form.formState.errors}
                  width={isMobile ? "243px" : "275px"}
                  noIcon={true}
                  defaultValue={
                    onEdit &&
                    formatDateYYYMMGG(new Date(locationSelected.ending_date))
                  }
                  constraints={{
                    required: true,
                  }}
                />
              </div>
            </div>
          </div>
          <div
            className=""
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
            }}
          >
            {onEdit && (
              <div
                className="offer-ctas-delete-box offLightBlue"
                style={{
                  marginTop: isMobile ? "20px" : "",
                }}
                onClick={handleOnDeleteClick}
              >
                <img
                  className="offer-ctas-delete-icon"
                  src={`${toIcons}/delete.svg`}
                />
                <p className="offer-ctas-delete-text">Delete location</p>
              </div>
            )}
            <button
              className="button2 proposal-modal-button-addon"
              type="submit"
              style={{
                marginTop: isMobile ? "20px" : "64px",
                width: isMobile ? "100%" : "",
              }}
            >
              <p className="button2-text">Save</p>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default LocationModal;
